import {
  Box,
  Button,
  Center,
  Grid,
  Group,
  Loader,
  Modal,
  MultiSelect,
  ScrollArea,
  Text,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import {
  EditProductPayload,
  getDescription,
  updateProduct,
} from "api/product.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_SUBMIT_ERR,
  MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
  MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
} from "common/constants";
import RichTextEditor, {
  RichTextEditorRef,
} from "components/elements/editor/RichTextEditor";
import React, { useEffect, useRef, useState } from "react";
import { useProductStore } from "stores/useProductStore";
import { ProductSummary } from "types/model.types";

interface IEditProductModalProps {
  opened: boolean;
  onClose: () => void;
  product: ProductSummary;
  onSave: (updatedProduct: any) => void;
}

const EditProductModal: React.FC<IEditProductModalProps> = ({
  opened,
  onClose,
  product,
  onSave,
}) => {
  const apiFetchRef = useRef(false);
  const editorRef = useRef<RichTextEditorRef>(null);
  const { brands, categories } = useProductStore();
  const [isLoading, setIsLoading] = useState(true);
  const normalize = (id: string) => id.trim().toLowerCase();

  // Define our useForm and must be declared before any UseEffects
  const form = useForm({
    initialValues: {
      title: product.title,
      brandIds:
        product.brandIds?.split(",").map(normalize).filter(Boolean) ?? [],
      categoryIds:
        product.categoryIds?.split(",").map(normalize).filter(Boolean) ?? [],

      description: "",
    },
    validate: {
      brandIds: () => null,
      categoryIds: () => null,
    },
  });
  useEffect(() => {
    if (!useProductStore.persist.hasHydrated()) return;

    useProductStore.getState().fetchBrands();
    useProductStore.getState().fetchCategories();
  }, []);

  useEffect(() => {
    if (!product || !opened) return;
    if (apiFetchRef.current) return;

    apiFetchRef.current = true;
    let mounted = true;

    (async () => {
      setIsLoading(true);

      try {
        const html = await safeApiCall(getDescription(product.id));
        if (!mounted) return;

        form.setFieldValue("description", html ?? ""); // syncs with form
        setIsLoading(false);
      } catch (error) {
        logError(error);
        setIsLoading(false);
      } finally {
        apiFetchRef.current = false;
      }
    })();

    form.setValues({
      title: product.title ?? "",
      brandIds: product.brandIds?.split(",").map(normalize) ?? [],
      categoryIds: product.categoryIds?.split(",").map(normalize) ?? [],
      description: product.productDesc ?? "",
    });

    return () => {
      mounted = false;
      apiFetchRef.current = false;
    };
  }, [product, opened]);

  //
  const handleSubmit = async () => {
    const brandIdCsv = form.values.brandIds.join(",");
    const categoryIdCsv = form.values.categoryIds.join(",");

    if (!form.validate().hasErrors) {
      const payload: EditProductPayload = {
        id: product.id,
        productDesc: form.values.description,
        brands: brandIdCsv,
        categories: categoryIdCsv,
        dImgName: product.detailedImageName,
        fImgName: product.featuredImageName,
      };
      try {
        const resp = await safeApiCall(updateProduct(payload));
        if (!resp?.success) {
          notifications.show({
            title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
            message: MSG_DEFAULT_FORM_SUBMIT_ERR,
            color: MantineNotificationColor.Error,
            position: MantineNotificationPosition.TopCenter,
          });
        } else {
          notifications.show({
            title: MSG_DEFAULT_FORM_SUBMIT_TITLE_SAVE,
            message: MSG_DEFAULT_FORM_SUBMIT_SUCCESS,
            color: MantineNotificationColor.Success,
            position: MantineNotificationPosition.TopCenter,
          });
        }
        onSave({
          ...product,
          brandIds: brandIdCsv,
          categoryIds: categoryIdCsv,
        });
        onClose();
      } catch (error) {
        logError(error);
      }
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      padding={0}
      fullScreen
      title="Edit Product"
    >
      <ScrollArea
        style={{
          height: "100%",
          flexGrow: 1,
          paddingBottom: "120px", // enough space for footer height
        }}
        type="always"
      >
        <Box p="md">
          <Title order={4} mb="sm">
            {form.values.title}
          </Title>
          <Grid mt="md">
            <Grid.Col span={6}>
              <Box pos="relative" style={{ zIndex: 10 }}>
                <MultiSelect
                  label="Brands"
                  data={brands.map((b) => ({
                    value: normalize(b.id.toString()),
                    label: b.name,
                  }))}
                  value={form.values.brandIds.map(normalize)}
                  onChange={(val) =>
                    form.setFieldValue("brandIds", val.map(normalize))
                  }
                  comboboxProps={{
                    withinPortal: true,
                    zIndex: 9999,
                  }}
                  searchable
                  clearable
                  placeholder="Select one or more brands"
                />
              </Box>
            </Grid.Col>
            <Grid.Col span={6}>
              <Box pos="relative" style={{ zIndex: 10 }}>
                <MultiSelect
                  label="Categories"
                  data={categories.map((c) => ({
                    value: normalize(c.id.toString()),
                    label: c.name,
                  }))}
                  value={form.values.categoryIds.map(normalize)}
                  onChange={(val) =>
                    form.setFieldValue("categoryIds", val.map(normalize))
                  }
                  comboboxProps={{
                    withinPortal: true,
                    zIndex: 9999,
                  }}
                  searchable
                  clearable
                  placeholder="Select one or more categories"
                />
              </Box>
            </Grid.Col>
          </Grid>

          <Text style={{ fontWeight: 600 }} mt="md">
            Description
          </Text>
          {isLoading ? (
            <Center>
              <Loader />
            </Center>
          ) : (
            <RichTextEditor
              ref={editorRef}
              content={form.values.description}
              onChange={(html) => form.setFieldValue("description", html)}
              placeholder="Edit CMS content..."
              variant="full"
            />
          )}

          <Group justify="flex-end" mt="xl">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>Save</Button>
          </Group>
        </Box>
      </ScrollArea>
    </Modal>
  );
};

export default EditProductModal;
